import { useState, type ReactNode } from 'react';
import { HelpCircle, X, Lightbulb, ChevronDown, ChevronUp } from 'lucide-react';

interface PageGuideProps {
  /** Уникальный ID для сохранения dismiss-состояния в localStorage */
  id: string;
  /** Заголовок баннера */
  title: string;
  /** Основной контент (2-4 предложения) */
  children: ReactNode;
  /** Развёрнутый контент (показывается по клику "Подробнее") */
  expandedContent?: ReactNode;
  /** Визуальный вариант */
  variant?: 'info' | 'tip';
  /** Можно ли скрыть (по умолчанию true) */
  dismissable?: boolean;
  /** CTA-кнопки внизу баннера */
  actions?: ReactNode;
}

const LS_PREFIX = 'help_dismissed_';

export function PageGuide({
  id,
  title,
  children,
  expandedContent,
  variant = 'info',
  dismissable = true,
  actions,
}: PageGuideProps) {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(`${LS_PREFIX}${id}`) === '1'; }
    catch { return false; }
  });
  const [expanded, setExpanded] = useState(false);

  if (dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(`${LS_PREFIX}${id}`, '1'); }
    catch { /* localStorage unavailable */ }
  };

  const isInfo = variant === 'info';
  const Icon = isInfo ? HelpCircle : Lightbulb;

  const borderColor = isInfo ? 'border-blue-200' : 'border-amber-200';
  const bgColor = isInfo ? 'bg-blue-50' : 'bg-amber-50';
  const iconColor = isInfo ? 'text-blue-500' : 'text-amber-500';
  const titleColor = isInfo ? 'text-blue-800' : 'text-amber-800';
  const bodyColor = isInfo ? 'text-blue-700' : 'text-amber-700';

  return (
    <div className={`rounded-lg border ${borderColor} ${bgColor} p-4 mb-4`} data-testid={`page-guide-${id}`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 ${iconColor} flex-shrink-0 mt-0.5`} />

        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${titleColor} mb-1`}>{title}</p>
          <div className={`text-xs ${bodyColor} leading-relaxed space-y-1`}>
            {children}
          </div>

          {expandedContent && expanded && (
            <div className={`text-xs ${bodyColor} leading-relaxed mt-2 pt-2 border-t ${borderColor} space-y-1`}>
              {expandedContent}
            </div>
          )}

          <div className="flex items-center gap-3 mt-2 flex-wrap">
            {expandedContent && (
              <button
                type="button"
                onClick={() => setExpanded(v => !v)}
                className={`text-xs font-medium ${titleColor} hover:underline flex items-center gap-1`}
              >
                {expanded ? (
                  <><ChevronUp className="w-3 h-3" />Свернуть</>
                ) : (
                  <><ChevronDown className="w-3 h-3" />Подробнее</>
                )}
              </button>
            )}
            {actions}
          </div>
        </div>

        {dismissable && (
          <button
            type="button"
            onClick={handleDismiss}
            className={`${bodyColor} hover:${titleColor} p-0.5 rounded transition-colors flex-shrink-0`}
            aria-label="Скрыть подсказку"
            title="Скрыть"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
